import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SkeletonRows } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import type { User } from '@supabase/supabase-js';
import type { Area } from 'react-easy-crop';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAll';
import { timeAgo } from '../lib/timeAgo';
import { useToast } from '../lib/useToast';
import { VenuePhotoCropModal } from '../components/VenuePhotoCropModal';
import { cropImageToJpegBlob } from '../lib/cropImage';
import { resolveCityHeroCropUrl, uploadCityHeroBlob } from '../lib/cityPhotoCrop';
import { ExportButton } from '../components/ExportButton';
import { StatCard } from '../ui/StatCard';
import { EmptyState } from '../ui/EmptyState';
import {
  CityHeroPickerModal,
  CITY_HERO_CROP_ASPECT,
  type CityVenuePhotoOption,
} from '../components/CityHeroPickerModal';
import { CityVenueDetailModal, type VenueDetailSeed } from '../components/CityVenueDetailModal';

/**
 * Schema:
 * - cities: id, name, filters_unlocked
 * - venues: id, city_id → cities.id
 * - venue_logs or logs: id, user_id, venue_id (logs: optional deleted_at)
 * - profiles.city: text like "Chicago, IL, USA" — matched to cities.name via RPC (ILIKE name || ',%')
 *
 * Requires DB function `admin_home_city_user_counts` — see supabase/sql/admin_home_city_user_counts.sql
 */

type HomeCityRpcRow = { id: string; home_city_users: number | string };

function rpcHomeCityCountsMissing(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('admin_home_city_user_counts') ||
    m.includes('could not find the function') ||
    m.includes('function public.admin_home_city_user_counts') ||
    m.includes('42883')
  );
}

async function fetchHomeCityUserCountsViaRpc(): Promise<{
  byCityId: Map<string, number>;
  homeCityNote: string | null;
}> {
  const { data, error } = await supabase.rpc('admin_home_city_user_counts');
  if (error) {
    const msg = error.message;
    if (rpcHomeCityCountsMissing(msg)) {
      return {
        byCityId: new Map(),
        homeCityNote:
          'Home city users are 0 until the database function exists. In Supabase → SQL, run `supabase/sql/admin_home_city_user_counts.sql`, then reload.',
      };
    }
    throw new Error(msg);
  }
  const byCityId = new Map<string, number>();
  for (const raw of (data ?? []) as HomeCityRpcRow[]) {
    const id = raw?.id;
    if (id == null || id === '') continue;
    const n = raw.home_city_users;
    const count = typeof n === 'number' ? n : Number(n);
    byCityId.set(String(id), Number.isFinite(count) ? count : 0);
  }
  return { byCityId, homeCityNote: null };
}

type CityPhotoFilterKey = 'pending_photos' | 'google_only' | 'single_photo_only';

const CITY_PHOTO_FILTER_OPTIONS: { key: CityPhotoFilterKey; label: string; title: string }[] = [
  {
    key: 'pending_photos',
    label: 'Pending photos only',
    title: 'Only cities with at least one photo awaiting approval',
  },
  {
    key: 'google_only',
    label: 'Using Google photo',
    title: 'No approved custom photo yet (Google may be live)',
  },
  {
    key: 'single_photo_only',
    label: 'Single photo only',
    title: 'Exactly one live hero image — not a multi-photo carousel',
  },
];

type CityRow = {
  id: string;
  name: string | null;
  filters_unlocked: boolean | null;
  going_tonight_enabled: boolean | null;
  hero_image_url: string | null;
  google_fetch_disabled: boolean | null;
  // Additive (guarded): may be absent on older schemas — always treated as nullable.
  featured: boolean | null;
  featured_tier: number | null;
  region: string | null;
  country_code: string | null;
  total_shado_ratings: number | null;
};

/**
 * The bulk venue fetch below pulls EVERY venue that has a `city_id` (37,249 rows in production,
 * 2026-08-01 — not the full 3.3M `venues` table, which is why this page is viable at all). Keep
 * this projection narrow: each added column is paid 37k times, so only short scalars belong here.
 * Anything wide (photo URLs, addresses) is fetched per-venue in `CityVenueDetailModal` instead.
 */
type VenueInCity = {
  id: string;
  city_id: string;
  venue_name_user_input: string | null;
  venue_type: string | null;
  venue_shado_score: number | null;
};

type RatingEvent = { id: string; user_id: string | null; venue_id: string | null };

/** One venue from admin_city_venues(p_city_id) — the lazy per-city list the fast path uses. */
type LazyCityVenue = {
  id: string;
  venue_name_user_input: string | null;
  venue_type: string | null;
  venue_shado_score: number | null;
  ratings: number;
  unique_raters: number;
};

/** One row from admin_city_overview_stats() — the server-side rollup for the table. */
type CityStatsRpcRow = {
  city_id: string;
  total_ratings: number;
  unique_raters: number;
  venues_rated: number;
};

type VenueRatingStats = { ratings: number; uniqueRaters: number };

function buildVenueRatingStats(events: RatingEvent[]): Record<string, VenueRatingStats> {
  const m = new Map<string, { ratings: number; raters: Set<string> }>();
  for (const ev of events) {
    const vid = ev.venue_id;
    if (!vid) continue;
    let a = m.get(vid);
    if (!a) {
      a = { ratings: 0, raters: new Set() };
      m.set(vid, a);
    }
    a.ratings += 1;
    if (ev.user_id) a.raters.add(ev.user_id);
  }
  const out: Record<string, VenueRatingStats> = {};
  m.forEach((a, vid) => {
    out[vid] = { ratings: a.ratings, uniqueRaters: a.raters.size };
  });
  return out;
}

type CityOverviewRow = CityRow & {
  totalRatings: number;
  uniqueRaters: number;
  venuesRated: number;
  homeCityUsers: number;
};

type PendingCityPhotoRow = {
  id: string;
  city_id: string;
  user_id: string | null;
  source: string | null;
  public_url: string | null;
  created_at: string;
};

type CityTableSortMode = 'ratings' | 'raters' | 'venues' | 'pending' | 'home' | 'alpha';

async function loadRatingEvents(): Promise<RatingEvent[]> {
  try {
    return await fetchAllRows<RatingEvent>((from, to) =>
      supabase.from('venue_logs').select('id, user_id, venue_id').range(from, to),
    );
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    if (
      msg.includes('venue_logs') ||
      msg.includes('Could not find the table') ||
      msg.includes('schema cache')
    ) {
      return await fetchAllRows<RatingEvent>((from, to) =>
        supabase.from('logs').select('id, user_id, venue_id').is('retired_at', null).range(from, to),
      );
    }
    throw e;
  }
}

const CITY_BASE_SELECT =
  'id, name, filters_unlocked, going_tonight_enabled, hero_image_url, google_fetch_disabled';
const CITY_EXTENDED_SELECT =
  'id, name, filters_unlocked, going_tonight_enabled, hero_image_url, google_fetch_disabled, featured, featured_tier, region, country_code, total_shado_ratings';

/**
 * Load cities, preferring the richer column set. If any of the additive columns
 * (featured / featured_tier / region / country_code / total_shado_ratings) is absent
 * in this environment, PostgREST errors on the whole select — so we transparently
 * fall back to the base select and surface those fields as null. Default behavior is
 * unchanged either way.
 */
async function loadCities(): Promise<CityRow[]> {
  const normalize = (raw: Partial<CityRow> & { id: string }): CityRow => ({
    id: raw.id,
    name: raw.name ?? null,
    filters_unlocked: raw.filters_unlocked ?? null,
    going_tonight_enabled: raw.going_tonight_enabled ?? null,
    hero_image_url: raw.hero_image_url ?? null,
    google_fetch_disabled: raw.google_fetch_disabled ?? null,
    featured: raw.featured ?? null,
    featured_tier: typeof raw.featured_tier === 'number' ? raw.featured_tier : null,
    region: raw.region ?? null,
    country_code: raw.country_code ?? null,
    total_shado_ratings:
      typeof raw.total_shado_ratings === 'number' ? raw.total_shado_ratings : null,
  });
  try {
    const rows = await fetchAllRows<Partial<CityRow> & { id: string }>((from, to) =>
      supabase
        .from('cities')
        .select(CITY_EXTENDED_SELECT)
        .order('name', { ascending: true })
        .range(from, to),
    );
    return rows.map(normalize);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    // A missing additive column (PostgREST 42703 / "column ... does not exist") must not
    // break the page — retry with only the always-present base columns.
    if (
      msg.includes('does not exist') ||
      msg.includes('42703') ||
      msg.includes('schema cache') ||
      msg.toLowerCase().includes('column')
    ) {
      const rows = await fetchAllRows<Partial<CityRow> & { id: string }>((from, to) =>
        supabase
          .from('cities')
          .select(CITY_BASE_SELECT)
          .order('name', { ascending: true })
          .range(from, to),
      );
      return rows.map(normalize);
    }
    throw e;
  }
}

function pickString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
}

function liveHeroUrl(c: Pick<CityRow, 'hero_image_url'>): string | null {
  return pickString(c.hero_image_url);
}

function hasApprovedCustomCityHero(c: CityRow, approvedCityPhotoCount: number): boolean {
  if (approvedCityPhotoCount > 0) return true;
  if (c.google_fetch_disabled) return true;
  const hero = pickString(c.hero_image_url);
  return Boolean(hero?.includes('/user-submissions/'));
}

function stillOnGoogleCity(c: CityRow, approvedCityPhotoCount: number): boolean {
  return !hasApprovedCustomCityHero(c, approvedCityPhotoCount);
}

function cityUsesExactlyOneLiveImage(c: CityRow, approvedCityPhotoCount: number): boolean {
  if (approvedCityPhotoCount >= 2) return false;
  if (approvedCityPhotoCount === 1) return true;
  return Boolean(liveHeroUrl(c));
}

function cityMatchesActivePhotoFilters(
  c: CityRow,
  approvedCityPhotoCount: number,
  pendingCount: number,
  active: readonly CityPhotoFilterKey[],
): boolean {
  if (active.length === 0) return true;
  for (const f of active) {
    if (f === 'pending_photos' && pendingCount < 1) return false;
    if (f === 'google_only' && !stillOnGoogleCity(c, approvedCityPhotoCount)) return false;
    if (f === 'single_photo_only' && !cityUsesExactlyOneLiveImage(c, approvedCityPhotoCount)) return false;
  }
  return true;
}

async function fetchApprovedCityPhotoCountsByCity(): Promise<Map<string, number>> {
  const m = new Map<string, number>();
  const rows = await fetchAllRows<{ city_id: string | null }>((from, to) =>
    supabase.from('city_photos').select('city_id').eq('approved', true).range(from, to),
  );
  for (const r of rows) {
    const id = r.city_id;
    if (!id) continue;
    m.set(id, (m.get(id) ?? 0) + 1);
  }
  return m;
}

function citySourceLabel(src: string | null): string {
  const s = (src ?? '').toLowerCase();
  if (s === 'city_upload') return 'City upload';
  if (s === 'venue_upload') return 'Venue upload';
  if (s === 'rating_photo') return 'From rating';
  return pickString(src) ?? '—';
}

export function CityOverviews({
  canEditCityFilters = true,
  user,
}: {
  canEditCityFilters?: boolean;
  user: User;
}) {
  const [rows, setRows] = useState<CityOverviewRow[]>([]);
  const [venuesInCities, setVenuesInCities] = useState<VenueInCity[]>([]);
  const [venueRatingStats, setVenueRatingStats] = useState<Record<string, VenueRatingStats>>({});
  /** True only when admin_city_overview_stats is missing (env without the SQL): the page falls
   * back to the old 37k-row client sweep, and the bulk state above stays populated. */
  const [legacyBulkMode, setLegacyBulkMode] = useState(false);
  const [lazyCityVenues, setLazyCityVenues] = useState<Record<string, LazyCityVenue[]>>({});
  const [pendingCityPhotos, setPendingCityPhotos] = useState<PendingCityPhotoRow[]>([]);
  const [approvedCityPhotoCountByCity, setApprovedCityPhotoCountByCity] = useState<Map<string, number>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyCityId, setBusyCityId] = useState<string | null>(null);
  const [homeCityNote, setHomeCityNote] = useState<string | null>(null);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  const [citySearch, setCitySearch] = useState('');
  const [debouncedCitySearch, setDebouncedCitySearch] = useState('');
  const [activeCityPhotoFilters, setActiveCityPhotoFilters] = useState<CityPhotoFilterKey[]>([]);
  const [cityTableSort, setCityTableSort] = useState<CityTableSortMode>('ratings');
  const [cityTableSortDir, setCityTableSortDir] = useState<'asc' | 'desc'>('desc');
  /** Column-header sorting (Evan 2026-10-07): click sets the column (numeric columns open
   * descending, City ascending); clicking the active column flips direction. */
  const sortByColumn = useCallback(
    (key: CityTableSortMode) => {
      setCityTableSort((prev) => {
        if (prev === key) {
          setCityTableSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
          return prev;
        }
        setCityTableSortDir(key === 'alpha' ? 'asc' : 'desc');
        return key;
      });
    },
    [],
  );
  // Additive filters — every one defaults to "no filter applied" so the on-load view is unchanged.
  const [tierFilter, setTierFilter] = useState<'' | '1' | '2' | '3'>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [unlockedFilter, setUnlockedFilter] = useState<'all' | 'on' | 'off'>('all');
  const [heroFilter, setHeroFilter] = useState<'all' | 'custom'>('all');
  const [minRatingsInput, setMinRatingsInput] = useState<string>('');
  const [actionCityPhotoId, setActionCityPhotoId] = useState<string | null>(null);
  const [cropPhoto, setCropPhoto] = useState<PendingCityPhotoRow | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [cropBusy, setCropBusy] = useState(false);
  // Drill-down state — all of it scoped to the OPEN city, so the all-cities view is untouched.
  const [cityVenuePhotos, setCityVenuePhotos] = useState<CityVenuePhotoOption[]>([]);
  const [cityVenuePhotosLoading, setCityVenuePhotosLoading] = useState(false);
  const [cityVenuePhotosError, setCityVenuePhotosError] = useState<string | null>(null);
  const [heroPickerOpen, setHeroPickerOpen] = useState(false);
  const [detailVenue, setDetailVenue] = useState<VenueDetailSeed | null>(null);

  /**
   * Approved venue photos for the OPEN city — the candidate pool for "Set city photo", and the
   * count on its button. Scoped to one city and capped, so it never scans: PostgREST drives off
   * `venue_photos` (245 approved rows in production, 2026-08-01) via
   * `venue_photos_venue_approved_idx`, then inner-joins `venues` by primary key.
   */
  useEffect(() => {
    // Leaving a city (or switching to another) must not carry its drill-downs over — otherwise
    // the picker re-opens on the NEXT city with the previous city's selection still armed.
    setHeroPickerOpen(false);
    setDetailVenue(null);
    if (!selectedCityId) {
      setCityVenuePhotos([]);
      setCityVenuePhotosError(null);
      return;
    }
    let cancelled = false;
    setCityVenuePhotosLoading(true);
    setCityVenuePhotosError(null);
    void (async () => {
      try {
        const { data, error: e } = await supabase
          .from('venue_photos')
          .select('id, venue_id, public_url, created_at, venues!inner(id, city_id, venue_name_user_input)')
          .eq('approved', true)
          .eq('venues.city_id', selectedCityId)
          .order('created_at', { ascending: false })
          .limit(300);
        if (e) throw e;
        if (cancelled) return;
        // PostgREST types a to-one embed as an object, but the generated client types widen it to
        // `unknown`; normalize defensively so an array-shaped embed can't blow up the mapper.
        const rowsOut: CityVenuePhotoOption[] = ((data ?? []) as Array<Record<string, unknown>>).map(
          (r) => {
            const embed = r.venues;
            const venue = (Array.isArray(embed) ? embed[0] : embed) as
              | { venue_name_user_input?: string | null }
              | undefined;
            return {
              id: String(r.id),
              venue_id: String(r.venue_id ?? ''),
              venue_name: venue?.venue_name_user_input ?? null,
              public_url: typeof r.public_url === 'string' ? r.public_url : null,
              created_at: String(r.created_at ?? ''),
            };
          },
        );
        setCityVenuePhotos(rowsOut);
      } catch (e: unknown) {
        if (cancelled) return;
        setCityVenuePhotos([]);
        setCityVenuePhotosError(
          e instanceof Error ? e.message : 'Failed to load approved venue photos for this city',
        );
      } finally {
        if (!cancelled) setCityVenuePhotosLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCityId]);

  // Resolve a CORS-safe (signed) crop source whenever a photo is opened in the crop modal.
  useEffect(() => {
    if (!cropPhoto?.public_url) {
      setCropImageUrl(null);
      return;
    }
    let cancelled = false;
    void resolveCityHeroCropUrl(cropPhoto.public_url).then((url) => {
      if (!cancelled) setCropImageUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [cropPhoto?.public_url]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedCitySearch(citySearch.trim().toLowerCase()), 300);
    return () => window.clearTimeout(t);
  }, [citySearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHomeCityNote(null);
    try {
      // Fast path (2026-10-07): the per-city totals come from ONE admin_city_overview_stats call
      // instead of pulling every venue with a city_id (37,486 rows = ~38 serial pages) plus every
      // live log into the browser. The old sweep survives only as the fallback for an environment
      // where the RPC has not been applied.
      const [cities, statsRes, homeCityPack, pendingRes, approvedCityPhotoCounts] = await Promise.all([
        loadCities(),
        supabase.rpc('admin_city_overview_stats'),
        fetchHomeCityUserCountsViaRpc(),
        supabase
          .from('city_photos')
          .select('id, city_id, user_id, source, public_url, created_at')
          .eq('approved', false)
          .eq('rejected', false)
          .order('created_at', { ascending: false }),
        fetchApprovedCityPhotoCountsByCity(),
      ]);

      if (pendingRes.error) throw pendingRes.error;
      setPendingCityPhotos((pendingRes.data ?? []) as PendingCityPhotoRow[]);
      setApprovedCityPhotoCountByCity(approvedCityPhotoCounts);

      const homeByCity = homeCityPack.byCityId;
      if (homeCityPack.homeCityNote) setHomeCityNote(homeCityPack.homeCityNote);

      const byCity = new Map<string, { ratings: number; raters: number; venuesRated: number }>();
      if (statsRes.error) {
        const msg = statsRes.error.message ?? '';
        if (!/admin_city_overview_stats|42883|schema cache/i.test(msg)) throw statsRes.error;
        // Legacy fallback: identical to the pre-RPC behavior, numbers and all.
        setLegacyBulkMode(true);
        const venues = await fetchAllRows<VenueInCity>((from, to) =>
          supabase
            .from('venues')
            .select('id, city_id, venue_name_user_input, venue_type, venue_shado_score')
            .not('city_id', 'is', null)
            .range(from, to),
        );
        const events = await loadRatingEvents();
        const statsByVenue = buildVenueRatingStats(events);
        const venueStatsComplete: Record<string, VenueRatingStats> = { ...statsByVenue };
        for (const v of venues) {
          if (!venueStatsComplete[v.id]) {
            venueStatsComplete[v.id] = { ratings: 0, uniqueRaters: 0 };
          }
        }
        setVenuesInCities(venues);
        setVenueRatingStats(venueStatsComplete);

        const venueToCity = new Map<string, string>();
        for (const v of venues) {
          if (v.city_id) venueToCity.set(v.id, v.city_id);
        }
        const aggSets = new Map<string, { ratings: number; raters: Set<string>; venuesWithRatings: Set<string> }>();
        for (const ev of events) {
          const vid = ev.venue_id;
          if (!vid) continue;
          const cid = venueToCity.get(vid);
          if (!cid) continue;
          let a = aggSets.get(cid);
          if (!a) {
            a = { ratings: 0, raters: new Set(), venuesWithRatings: new Set() };
            aggSets.set(cid, a);
          }
          a.ratings += 1;
          a.venuesWithRatings.add(vid);
          if (ev.user_id) a.raters.add(ev.user_id);
        }
        aggSets.forEach((a, cid) => {
          byCity.set(cid, { ratings: a.ratings, raters: a.raters.size, venuesRated: a.venuesWithRatings.size });
        });
      } else {
        setLegacyBulkMode(false);
        setVenuesInCities([]);
        setVenueRatingStats({});
        for (const s of (statsRes.data ?? []) as CityStatsRpcRow[]) {
          byCity.set(String(s.city_id), {
            ratings: s.total_ratings,
            raters: s.unique_raters,
            venuesRated: s.venues_rated,
          });
        }
      }

      const merged: CityOverviewRow[] = cities.map((c) => {
        const agg = byCity.get(c.id);
        return {
          ...c,
          totalRatings: agg?.ratings ?? 0,
          uniqueRaters: agg?.raters ?? 0,
          venuesRated: agg?.venuesRated ?? 0,
          homeCityUsers: homeByCity.get(String(c.id)) ?? 0,
        };
      });

      merged.sort((a, b) => b.totalRatings - a.totalRatings);
      setRows(merged);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load city overviews');
      setRows([]);
      setVenuesInCities([]);
      setVenueRatingStats({});
      setPendingCityPhotos([]);
      setApprovedCityPhotoCountByCity(new Map());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const { showError, showSuccess, toastNode } = useToast();

  async function toggleFiltersUnlocked(c: CityOverviewRow, next: boolean) {
    setBusyCityId(c.id);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_set_city_filters_unlocked', {
        p_city_id: c.id,
        p_unlocked: next,
      });
      if (rpcErr) {
        if (
          rpcErr.message.includes('admin_set_city_filters_unlocked') ||
          rpcErr.message.includes('42883')
        ) {
          throw new Error(
            'City filter toggle is not set up yet. Run supabase/sql/city_filters_unlocked_admin.sql in Supabase.',
          );
        }
        throw rpcErr;
      }
      setRows((prev) => prev.map((r) => (r.id === c.id ? { ...r, filters_unlocked: next } : r)));
      showSuccess(next ? 'Filters unlocked for this city' : 'Filters locked for this city');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyCityId(null);
    }
  }

  /**
   * Per-city rollout switch for the venue page's "Are you going tonight?" row.
   *
   * A city-level flag rather than a global one because the row is only worth showing where enough
   * people are out on the same night for a count to mean anything — one "1 going" on a venue in a
   * city with four users reads as nobody rather than as somebody. Defaults to false everywhere, so
   * a city appears only when it is deliberately switched on here.
   *
   * `admin_set_city_going_tonight` is `is_admin()`-gated server-side; `canEditCityFilters` only
   * decides whether to draw the switch, and is never the thing that enforces it.
   */
  async function toggleGoingTonight(c: CityOverviewRow, next: boolean) {
    setBusyCityId(c.id);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_set_city_going_tonight', {
        p_city_id: c.id,
        p_enabled: next,
      });
      if (rpcErr) throw rpcErr;
      setRows((prev) =>
        prev.map((r) => (r.id === c.id ? { ...r, going_tonight_enabled: next } : r)),
      );
      showSuccess(
        next
          ? `"Going tonight" is live in ${c.name ?? 'this city'}`
          : `"Going tonight" is off in ${c.name ?? 'this city'}`,
      );
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyCityId(null);
    }
  }

  const pendingByCity = useMemo(() => {
    const m = new Map<string, PendingCityPhotoRow[]>();
    for (const p of pendingCityPhotos) {
      const cid = p.city_id;
      if (!cid) continue;
      if (!m.has(cid)) m.set(cid, []);
      m.get(cid)!.push(p);
    }
    return m;
  }, [pendingCityPhotos]);

  const pendingPhotoSummary = useMemo(() => {
    const total = pendingCityPhotos.length;
    const cityIds = new Set<string>();
    for (const p of pendingCityPhotos) {
      if (p.city_id) cityIds.add(p.city_id);
    }
    return { total, citiesWithPending: cityIds.size };
  }, [pendingCityPhotos]);

  const toggleCityPhotoFilter = useCallback((key: CityPhotoFilterKey) => {
    setActiveCityPhotoFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const displayRows = useMemo(() => {
    let list = rows;
    if (debouncedCitySearch) {
      list = list.filter((r) => (r.name ?? '').toLowerCase().includes(debouncedCitySearch));
    }
    if (activeCityPhotoFilters.length > 0) {
      list = list.filter((r) =>
        cityMatchesActivePhotoFilters(
          r,
          approvedCityPhotoCountByCity.get(r.id) ?? 0,
          pendingByCity.get(r.id)?.length ?? 0,
          activeCityPhotoFilters,
        ),
      );
    }
    if (tierFilter !== '') {
      const wanted = Number(tierFilter);
      list = list.filter((r) => r.featured_tier === wanted);
    }
    if (regionFilter !== '') {
      list = list.filter((r) => (r.region ?? '') === regionFilter);
    }
    if (unlockedFilter !== 'all') {
      const wantOn = unlockedFilter === 'on';
      list = list.filter((r) => Boolean(r.filters_unlocked) === wantOn);
    }
    if (heroFilter === 'custom') {
      list = list.filter((r) =>
        hasApprovedCustomCityHero(r, approvedCityPhotoCountByCity.get(r.id) ?? 0),
      );
    }
    {
      const minRatings = Number.parseInt(minRatingsInput, 10);
      if (Number.isFinite(minRatings) && minRatings > 0) {
        list = list.filter((r) => r.totalRatings >= minRatings);
      }
    }
    list = [...list];
    const dir = cityTableSortDir === 'asc' ? 1 : -1;
    const num = (r: (typeof list)[number]): number =>
      cityTableSort === 'ratings'
        ? r.totalRatings
        : cityTableSort === 'raters'
          ? r.uniqueRaters
          : cityTableSort === 'venues'
            ? r.venuesRated
            : cityTableSort === 'home'
              ? r.homeCityUsers
              : (pendingByCity.get(r.id)?.length ?? 0);
    if (cityTableSort === 'alpha') {
      list.sort((a, b) => dir * (a.name ?? '').localeCompare(b.name ?? ''));
    } else {
      list.sort((a, b) => {
        const d = num(b) - num(a);
        if (d !== 0) return dir === -1 ? d : -d;
        return (a.name ?? '').localeCompare(b.name ?? '');
      });
    }
    return list;
  }, [
    rows,
    debouncedCitySearch,
    activeCityPhotoFilters,
    cityTableSort,
    cityTableSortDir,
    pendingByCity,
    approvedCityPhotoCountByCity,
    tierFilter,
    regionFilter,
    unlockedFilter,
    heroFilter,
    minRatingsInput,
  ]);

  // Distinct regions present in the loaded cities, for the additive region filter dropdown.
  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const reg = pickString(r.region);
      if (reg) set.add(reg);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const anyExtraFilterActive =
    tierFilter !== '' ||
    regionFilter !== '' ||
    unlockedFilter !== 'all' ||
    heroFilter !== 'all' ||
    minRatingsInput.trim() !== '';

  const cityCount = useMemo(() => rows.length, [rows]);

  const selectedCity = useMemo(
    () => (selectedCityId ? rows.find((r) => r.id === selectedCityId) ?? null : null),
    [rows, selectedCityId],
  );

  /** The hero the app is serving for this city right now (`cities.hero_image_url`). */
  const selectedCityHeroUrl = selectedCity ? liveHeroUrl(selectedCity) : null;

  // Fast path: fetch the expanded city's venues on demand (one admin_city_venues call, cached per
  // city for the session). Legacy mode already holds the full bulk data in memory.
  useEffect(() => {
    if (!selectedCityId || legacyBulkMode || lazyCityVenues[selectedCityId]) return;
    const cityId = selectedCityId;
    let cancelled = false;
    void (async () => {
      try {
        const { data, error: rpcErr } = await supabase.rpc('admin_city_venues', { p_city_id: cityId });
        if (rpcErr) throw rpcErr;
        if (!cancelled) setLazyCityVenues((m) => ({ ...m, [cityId]: (data ?? []) as LazyCityVenue[] }));
      } catch {
        // Leave it unset so re-opening the city retries; the detail panel just shows no venues.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCityId, legacyBulkMode, lazyCityVenues]);

  const venuesInSelectedCity = useMemo(() => {
    if (!selectedCityId) return [];
    const list = legacyBulkMode
      ? venuesInCities
          .filter((v) => v.city_id === selectedCityId)
          .map((v) => {
            const s = venueRatingStats[v.id] ?? { ratings: 0, uniqueRaters: 0 };
            return {
              id: v.id,
              name: v.venue_name_user_input?.trim() || 'Untitled venue',
              venueType: pickString(v.venue_type),
              score: typeof v.venue_shado_score === 'number' ? v.venue_shado_score : null,
              ratings: s.ratings,
              uniqueRaters: s.uniqueRaters,
            };
          })
      : (lazyCityVenues[selectedCityId] ?? []).map((v) => ({
          id: v.id,
          name: v.venue_name_user_input?.trim() || 'Untitled venue',
          venueType: pickString(v.venue_type),
          score: typeof v.venue_shado_score === 'number' ? v.venue_shado_score : null,
          ratings: v.ratings,
          uniqueRaters: v.unique_raters,
        }));
    list.sort((a, b) => {
      if (b.ratings !== a.ratings) return b.ratings - a.ratings;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [selectedCityId, legacyBulkMode, venuesInCities, venueRatingStats, lazyCityVenues]);

  /**
   * City-level rollups for the open city. Derived from data already in memory (the bulk venue
   * fetch + the rating events) — zero extra requests. `venue_type` is only ever 'bar' | 'club' |
   * NULL in production (2026-08-01), so the breakdown is those three buckets and nothing else.
   */
  const selectedCityStats = useMemo(() => {
    let bars = 0;
    let clubs = 0;
    let untyped = 0;
    let scored = 0;
    for (const v of venuesInSelectedCity) {
      const t = (v.venueType ?? '').toLowerCase();
      if (t === 'bar') bars += 1;
      else if (t === 'club') clubs += 1;
      else untyped += 1;
      if (typeof v.score === 'number') scored += 1;
    }
    return { venues: venuesInSelectedCity.length, bars, clubs, untyped, scored };
  }, [venuesInSelectedCity]);

  const selectedCityPending = selectedCityId ? pendingByCity.get(selectedCityId) ?? [] : [];

  async function approveCityPhoto(
    photo: PendingCityPhotoRow,
    cropped?: { publicUrl: string; storagePath: string },
  ) {
    if (!photo.city_id) return;
    setActionCityPhotoId(photo.id);
    try {
      const { data: cityRow, error: readErr } = await supabase
        .from('cities')
        .select('id, hero_image_url')
        .eq('id', photo.city_id)
        .maybeSingle();
      if (readErr) throw readErr;
      const oldGoogleHero = cityRow?.hero_image_url ?? null;

      // When approving a cropped version, point the pending row at the cropped object first so the
      // existing approve RPC (which reads city_photos.public_url) sets the city hero to the crop.
      // Admins have an UPDATE policy on city_photos (city_photos_admin_update).
      if (cropped) {
        const { error: updErr } = await supabase
          .from('city_photos')
          .update({ public_url: cropped.publicUrl, storage_path: cropped.storagePath })
          .eq('id', photo.id);
        if (updErr) throw updErr;
      }

      const { data: approvedRows, error: rpcErr } = await supabase.rpc('admin_approve_city_photo', {
        p_photo_id: photo.id,
      });
      if (rpcErr) {
        if (
          rpcErr.message.includes('admin_approve_city_photo') ||
          rpcErr.message.includes('42883')
        ) {
          throw new Error(
            'City photo approval is not set up yet. Run supabase/sql/city_photo_admin.sql in Supabase.',
          );
        }
        throw rpcErr;
      }

      const approved = (approvedRows as { city_id: string; public_url: string }[] | null)?.[0];
      const url = pickString(approved?.public_url ?? photo.public_url);
      if (!url) throw new Error('Approve succeeded but no public URL returned');

      if (oldGoogleHero) {
        try {
          const marker = '/city-heroes/';
          const idx = oldGoogleHero.indexOf(marker);
          if (idx >= 0) {
            const path = oldGoogleHero.slice(idx + marker.length);
            if (!path.startsWith('user-submissions/')) {
              await supabase.storage.from('city-heroes').remove([path]);
            }
          }
        } catch {
          /* non-blocking */
        }
      }

      setPendingCityPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setRows((prev) =>
        prev.map((r) =>
          r.id === photo.city_id
            ? { ...r, hero_image_url: url, google_fetch_disabled: true }
            : r,
        ),
      );
      setApprovedCityPhotoCountByCity((prev) => {
        const next = new Map(prev);
        const cid = photo.city_id;
        next.set(cid, (next.get(cid) ?? 0) + 1);
        return next;
      });
      showSuccess('City photo approved');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setActionCityPhotoId(null);
    }
  }

  async function confirmCropAndApproveCity(pixelCrop: Area) {
    if (!cropPhoto?.public_url || !cropPhoto.city_id) return;
    setCropBusy(true);
    try {
      const sourceUrl = await resolveCityHeroCropUrl(cropPhoto.public_url);
      const blob = await cropImageToJpegBlob(sourceUrl, pixelCrop);
      // Upload under user-submissions/ so the approved hero is treated as a custom (non-Google)
      // photo, and the existing approve cleanup never deletes it as a stale Google hero.
      const uploadPath = `user-submissions/${cropPhoto.city_id}/admin-${cropPhoto.id}-${Date.now()}.jpg`;
      const { publicUrl, storagePath } = await uploadCityHeroBlob(uploadPath, blob);
      await approveCityPhoto(cropPhoto, { publicUrl, storagePath });
      setCropPhoto(null);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Crop or approve failed');
    } finally {
      setCropBusy(false);
    }
  }

  async function rejectCityPhoto(photo: PendingCityPhotoRow) {
    setActionCityPhotoId(photo.id);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_reject_city_photo', { p_photo_id: photo.id });
      if (rpcErr) {
        if (
          rpcErr.message.includes('admin_reject_city_photo') ||
          rpcErr.message.includes('42883')
        ) {
          const { error } = await supabase.from('city_photos').update({ rejected: true }).eq('id', photo.id);
          if (error) throw error;
        } else {
          throw rpcErr;
        }
      }
      setPendingCityPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      showSuccess('City photo rejected');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Reject failed');
    } finally {
      setActionCityPhotoId(null);
    }
  }

  return (
    <div className="admin-page max-w-6xl mx-auto">
      <PageHeader
        title="Cities"
        subtitle={
          selectedCity ? (
            <>
              Venues in <span className="text-white">{selectedCity.name?.trim() || '—'}</span>, ranked by total
              ratings (high → low).
            </>
          ) : (
            <>
              Ratings and users by preloaded city ({cityCount} cities). Click a city to see venues. Sorted by total
              Shado ratings.
            </>
          )
        }
      />

      {error ? <ErrorBanner className="mb-5">{error}</ErrorBanner> : null}

      {homeCityNote && !error ? (
        <div className="mb-5 max-w-3xl rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
          {homeCityNote}
        </div>
      ) : null}

      {selectedCityId ? (
        <div className="mb-5">
          <button
            type="button"
            onClick={() => setSelectedCityId(null)}
            className="admin-btn admin-btn-secondary"
          >
            ← All cities
          </button>
        </div>
      ) : null}

      {!selectedCityId && !loading ? (
        <div className="mb-4 space-y-3 max-w-xl">
          <label htmlFor="city-admin-search" className="admin-table-head block">
            Search cities
          </label>
          <input
            id="city-admin-search"
            type="search"
            value={citySearch}
            onChange={(e) => setCitySearch(e.target.value)}
            placeholder="Filter by city name…"
            className="admin-input admin-input-lg"
          />
        </div>
      ) : null}

      {!selectedCityId && !loading ? (
        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3 gap-y-3">
            <label className="flex items-center gap-2 text-xs text-muted shrink-0">
              <span className="text-mutedDim">Sort</span>
              <select
                value={cityTableSort}
                onChange={(e) => setCityTableSort(e.target.value as CityTableSortMode)}
                className="admin-input !w-auto !px-2 !text-xs"
              >
                <option value="ratings">Most rated</option>
                <option value="raters">Most unique raters</option>
                <option value="venues">Most venues rated</option>
                <option value="pending">Most pending photos</option>
                <option value="home">Most home-city users</option>
                <option value="alpha">Alphabetical</option>
              </select>
            </label>
            <span className="rounded-full border border-border/80 bg-white/[0.04] px-3 py-1 text-[11px] text-muted tabular-nums">
              {pendingPhotoSummary.total} pending across {pendingPhotoSummary.citiesWithPending} cities
            </span>
          </div>
          <div
            className="flex flex-wrap gap-x-4 gap-y-3"
            role="group"
            aria-label="Filter cities"
          >
            {CITY_PHOTO_FILTER_OPTIONS.map(({ key, label, title }) => (
              <label
                key={key}
                title={title}
                className="inline-flex cursor-pointer items-center gap-3 text-sm text-white/90 hover:text-white select-none"
              >
                <input
                  type="checkbox"
                  checked={activeCityPhotoFilters.includes(key)}
                  onChange={() => toggleCityPhotoFilter(key)}
                  className="h-4 w-4 shrink-0 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 gap-y-3">
            <label className="flex items-center gap-2 text-xs text-muted shrink-0">
              <span className="text-mutedDim">Tier</span>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value as '' | '1' | '2' | '3')}
                className="admin-input !w-auto !px-2 !text-xs"
              >
                <option value="">All tiers</option>
                <option value="1">Tier 1</option>
                <option value="2">Tier 2</option>
                <option value="3">Tier 3</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted shrink-0">
              <span className="text-mutedDim">Region</span>
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="admin-input !w-auto !px-2 !text-xs"
              >
                <option value="">All regions</option>
                {regionOptions.map((reg) => (
                  <option key={reg} value={reg}>
                    {reg}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted shrink-0">
              <span className="text-mutedDim">Filters</span>
              <select
                value={unlockedFilter}
                onChange={(e) => setUnlockedFilter(e.target.value as 'all' | 'on' | 'off')}
                className="admin-input !w-auto !px-2 !text-xs"
              >
                <option value="all">Any unlock</option>
                <option value="on">Unlocked</option>
                <option value="off">Locked</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted shrink-0">
              <span className="text-mutedDim">Hero</span>
              <select
                value={heroFilter}
                onChange={(e) => setHeroFilter(e.target.value as 'all' | 'custom')}
                className="admin-input !w-auto !px-2 !text-xs"
              >
                <option value="all">Any photo</option>
                <option value="custom">Custom hero only</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted shrink-0">
              <span className="text-mutedDim">Min ratings</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={minRatingsInput}
                onChange={(e) => setMinRatingsInput(e.target.value)}
                placeholder="0"
                className="admin-input !w-20 !px-2 !text-xs"
              />
            </label>
            {anyExtraFilterActive ? (
              <button
                type="button"
                onClick={() => {
                  setTierFilter('');
                  setRegionFilter('');
                  setUnlockedFilter('all');
                  setHeroFilter('all');
                  setMinRatingsInput('');
                }}
                className="rounded-lg border border-border bg-white/[0.04] px-3 py-1.5 text-xs text-muted hover:bg-white/[0.07] hover:text-white transition-colors"
              >
                Clear filters
              </button>
            ) : null}
            <span className="ml-auto">
              <ExportButton
                rows={displayRows}
                columns={[
                  { header: 'name', get: (r) => r.name?.trim() ?? '' },
                  { header: 'featured_tier', get: (r) => r.featured_tier ?? '' },
                  { header: 'region', get: (r) => r.region ?? '' },
                  { header: 'totalRatings', get: (r) => r.totalRatings },
                  { header: 'uniqueRaters', get: (r) => r.uniqueRaters },
                  { header: 'venuesRated', get: (r) => r.venuesRated },
                  { header: 'homeCityUsers', get: (r) => r.homeCityUsers },
                  {
                    header: 'filters_unlocked',
                    get: (r) => (r.filters_unlocked ? 'yes' : 'no'),
                  },
                  {
                    header: 'has-hero',
                    get: (r) =>
                      hasApprovedCustomCityHero(r, approvedCityPhotoCountByCity.get(r.id) ?? 0)
                        ? 'yes'
                        : 'no',
                  },
                ]}
                filename="shado-cities.csv"
                label="Export CSV"
                disabled={displayRows.length === 0}
              />
            </span>
          </div>
        </div>
      ) : null}

      {loading ? (
        <SkeletonRows rows={9} className="py-2" />
      ) : selectedCityId ? (
        <div className="space-y-10">
          {/* City overview — the "one more layer deep" pass for the city page itself: the live
              hero + the control that changes it, the city's own row metadata, and rollups over
              its venues. All derived from state already loaded, except the hero candidate pool
              (one scoped query in the effect above). */}
          <section>
            <h2 className="mb-4 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
              Overview
            </h2>
            <div className="mb-5 flex flex-col gap-5 rounded-xl border border-border bg-surface p-5 sm:flex-row">
              <div className="shrink-0">
                {/* Square preview because that is exactly how the app frames it —
                    `screens/city.tsx:3565`, `aspectRatio: 1` + `contentFit="cover"`. */}
                <div className="h-36 w-36 overflow-hidden rounded-xl border border-border bg-black">
                  {selectedCityHeroUrl ? (
                    <img
                      src={selectedCityHeroUrl}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-[11px] text-mutedDim">
                      no city photo
                    </div>
                  )}
                </div>
              </div>
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-mutedDim">
                  {selectedCity?.featured_tier ? (
                    <span className="rounded-full border border-border/80 bg-white/[0.04] px-2.5 py-0.5 text-muted">
                      Tier {selectedCity.featured_tier}
                    </span>
                  ) : null}
                  {pickString(selectedCity?.region ?? null) ? (
                    <span className="rounded-full border border-border/80 bg-white/[0.04] px-2.5 py-0.5 text-muted">
                      {selectedCity?.region}
                    </span>
                  ) : null}
                  {pickString(selectedCity?.country_code ?? null) ? (
                    <span className="rounded-full border border-border/80 bg-white/[0.04] px-2.5 py-0.5 text-muted">
                      {selectedCity?.country_code}
                    </span>
                  ) : null}
                  <span className="rounded-full border border-border/80 bg-white/[0.04] px-2.5 py-0.5 text-muted">
                    Filters {selectedCity?.filters_unlocked ? 'unlocked' : 'locked'}
                  </span>
                  <span className="rounded-full border border-border/80 bg-white/[0.04] px-2.5 py-0.5 text-muted">
                    {selectedCity && stillOnGoogleCity(selectedCity, approvedCityPhotoCountByCity.get(selectedCity.id) ?? 0)
                      ? 'Google photo live'
                      : 'Custom photo'}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed text-mutedDim">
                  Pick the city photo from any <span className="text-muted">approved</span> venue photo
                  in this city. Setting it writes through the admin approve RPC (a direct write to{' '}
                  <code className="text-muted">cities</code> is blocked by RLS) and turns Google fetch
                  off for this city.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setHeroPickerOpen(true)}
                    disabled={cityVenuePhotosLoading || cityVenuePhotos.length === 0}
                    className="admin-btn admin-btn-primary admin-btn-sm"
                    title={
                      cityVenuePhotos.length === 0
                        ? 'No approved venue photos in this city yet'
                        : 'Choose a city photo from approved venue photos'
                    }
                  >
                    Set city photo
                  </button>
                  <span className="text-[11px] text-mutedDim tabular-nums">
                    {cityVenuePhotosLoading
                      ? 'Loading approved venue photos…'
                      : `${cityVenuePhotos.length} approved venue photo${
                          cityVenuePhotos.length === 1 ? '' : 's'
                        } available`}
                  </span>
                </div>
                {cityVenuePhotosError ? (
                  <ErrorBanner className="!py-2 !text-[11px]">{cityVenuePhotosError}</ErrorBanner>
                ) : null}
              </div>
            </div>

            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
            >
              <StatCard label="Venues" value={selectedCityStats.venues} sub="with this city_id" />
              <StatCard
                label="Venues rated"
                value={selectedCity?.venuesRated ?? 0}
                sub={`${selectedCityStats.scored} carry a score`}
              />
              <StatCard label="Total ratings" value={selectedCity?.totalRatings ?? 0} sub="live log rows" />
              <StatCard label="Unique raters" value={selectedCity?.uniqueRaters ?? 0} />
              <StatCard label="Home city users" value={selectedCity?.homeCityUsers ?? 0} />
              <StatCard
                label="Bars / clubs"
                value={`${selectedCityStats.bars} / ${selectedCityStats.clubs}`}
                sub={`${selectedCityStats.untyped} untyped`}
              />
              <StatCard
                label="Approved photos"
                value={cityVenuePhotos.length}
                sub={`${selectedCityPending.length} city photo${
                  selectedCityPending.length === 1 ? '' : 's'
                } pending`}
              />
            </div>
          </section>

          {/* Pending photos render ABOVE the venue list. Mirrors the venue photo review flow:
              "Approve" opens a crop modal; confirming the crop uploads it and replaces the city
              hero (and disables Google fetch) via the existing admin_approve_city_photo RPC. */}
          <section>
            <div className="mb-4 flex items-center gap-3">
              <h2 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                Pending photos
              </h2>
              {selectedCityPending.length > 0 ? (
                <span className="rounded-full border border-border/80 bg-white/[0.04] px-2.5 py-0.5 text-[11px] text-muted tabular-nums">
                  {selectedCityPending.length} awaiting approval
                </span>
              ) : null}
            </div>
            {selectedCityPending.length === 0 ? (
              <p className="text-sm text-muted">No pending photos for this city.</p>
            ) : (
              <ul className="space-y-10">
                {selectedCityPending.map((ph) => (
                  <li
                    key={ph.id}
                    className="w-full max-w-[54rem] overflow-hidden rounded-xl border border-border bg-black"
                  >
                    {ph.public_url ? (
                      <img
                        src={ph.public_url}
                        alt=""
                        className="max-h-[min(61.5dvh,960px)] w-full object-contain object-center"
                      />
                    ) : (
                      <div className="flex min-h-[min(30dvh,270px)] items-center justify-center text-sm text-muted">
                        No image URL
                      </div>
                    )}
                    <div className="border-t border-border bg-canvas px-5 py-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <div className="admin-label-primary text-sm">{citySourceLabel(ph.source)}</div>
                          <div className="text-xs text-mutedDim mt-1">{timeAgo(ph.created_at)}</div>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <button
                            type="button"
                            disabled={actionCityPhotoId === ph.id || cropBusy}
                            onClick={() => setCropPhoto(ph)}
                            className="rounded-md bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-500 disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={actionCityPhotoId === ph.id}
                            onClick={() => void rejectCityPhoto(ph)}
                            className="rounded-md border border-border bg-white/[0.04] px-4 py-2 text-xs font-semibold text-muted hover:bg-white/[0.07] hover:text-white disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <h2 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">Venues</h2>
              <span className="text-[11px] text-mutedDim">Click a venue for its full record</span>
            </div>
            <div className="rounded-xl border border-border bg-surface overflow-x-auto">
              <table className="w-full border-collapse text-sm min-w-[680px]">
                <thead>
                  <tr className="admin-table-head">
                    <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">Venue</th>
                    <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">Type</th>
                    <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                      Shado score
                    </th>
                    <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                      Total ratings
                    </th>
                    <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                      Unique raters
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {venuesInSelectedCity.map((v, i) => (
                    <tr
                      key={v.id}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setDetailVenue({
                          id: v.id,
                          name: v.name,
                          ratings: v.ratings,
                          uniqueRaters: v.uniqueRaters,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDetailVenue({
                            id: v.id,
                            name: v.name,
                            ratings: v.ratings,
                            uniqueRaters: v.uniqueRaters,
                          });
                        }
                      }}
                      className={`border-b border-border/50 cursor-pointer transition-colors hover:bg-white/[0.06] ${
                        i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'
                      }`}
                    >
                      <td className="px-4 py-4 admin-label-primary">{v.name}</td>
                      <td className="px-4 py-4">
                        {v.venueType ? (
                          <span className="rounded-full border border-border/80 bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-muted">
                            {v.venueType}
                          </span>
                        ) : (
                          <span className="text-mutedDim">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right admin-metric">
                        {typeof v.score === 'number' ? v.score.toFixed(1) : '—'}
                      </td>
                      <td className="px-4 py-4 text-right admin-metric">{v.ratings}</td>
                      <td className="px-4 py-4 text-right admin-metric">{v.uniqueRaters}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {venuesInSelectedCity.length === 0 ? (
                <div className="px-4 py-8">
                  <EmptyState
                    title="No venues in this city"
                    hint="A venue joins a city when its `city_id` is set — check the venue's placement on the Venue Review page."
                  />
                </div>
              ) : null}
            </div>
          </section>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-surface overflow-x-auto">
          <table className="w-full border-collapse text-sm min-w-[920px]">
            <thead>
              <tr className="admin-table-head">
                <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">
                  <button type="button" onClick={() => sortByColumn('alpha')} className="hover:text-white">
                    City{cityTableSort === 'alpha' ? (cityTableSortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                  <button type="button" onClick={() => sortByColumn('ratings')} className="hover:text-white">
                    Total ratings{cityTableSort === 'ratings' ? (cityTableSortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </button>
                </th>
                <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                  <button type="button" onClick={() => sortByColumn('raters')} className="hover:text-white">
                    Unique raters{cityTableSort === 'raters' ? (cityTableSortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </button>
                </th>
                <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                  <button type="button" onClick={() => sortByColumn('venues')} className="hover:text-white">
                    Venues rated{cityTableSort === 'venues' ? (cityTableSortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </button>
                </th>
                <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                  <button type="button" onClick={() => sortByColumn('pending')} className="hover:text-white">
                    Pending photos{cityTableSort === 'pending' ? (cityTableSortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </button>
                </th>
                <th
                  className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-center w-12"
                  title="Google photo live"
                >
                  G
                </th>
                <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 text-right">
                  <button type="button" onClick={() => sortByColumn('home')} className="hover:text-white">
                    Home city users{cityTableSort === 'home' ? (cityTableSortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </button>
                </th>
                <th className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">
                  Filters unlocked
                </th>
                <th
                  className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5"
                  title='Shows the "Are you going tonight?" row on venue pages in this city'
                >
                  Going tonight
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted text-sm">
                    No cities found.
                  </td>
                </tr>
              ) : displayRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted text-sm">
                    No cities match these filters.
                  </td>
                </tr>
              ) : (
                displayRows.map((r, i) => (
                <tr
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedCityId(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedCityId(r.id);
                    }
                  }}
                  className={`border-b border-border/50 cursor-pointer transition-colors hover:bg-white/[0.06] ${
                    i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'
                  }`}
                >
                  <td className="px-4 py-4 admin-label-primary">
                    <div className="flex items-center gap-2">
                      <span>{r.name?.trim() || '—'}</span>
                      {r.featured_tier ? (
                        <span
                          className="shrink-0 rounded-full border border-border/80 bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-muted tabular-nums"
                          title={`Featured tier ${r.featured_tier}`}
                        >
                          T{r.featured_tier}
                        </span>
                      ) : null}
                    </div>
                    {pickString(r.region) ? (
                      <div className="mt-0.5 text-[11px] font-normal text-mutedDim">{r.region}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 text-right admin-metric">{r.totalRatings}</td>
                  <td className="px-4 py-4 text-right admin-metric">{r.uniqueRaters}</td>
                  <td className="px-4 py-4 text-right admin-metric">{r.venuesRated}</td>
                  <td className="px-4 py-4 text-right admin-metric tabular-nums">
                    {pendingByCity.get(r.id)?.length ?? 0}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {stillOnGoogleCity(r, approvedCityPhotoCountByCity.get(r.id) ?? 0) ? (
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full bg-accent"
                        title="Using Google photo"
                      />
                    ) : (
                      <span className="text-mutedDim">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right admin-metric">{r.homeCityUsers}</td>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    {canEditCityFilters ? (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={Boolean(r.filters_unlocked)}
                        aria-label={`Filters unlocked for ${r.name ?? r.id}`}
                        disabled={busyCityId === r.id}
                        onClick={() => void toggleFiltersUnlocked(r, !r.filters_unlocked)}
                        className={`flex h-7 w-12 shrink-0 items-center rounded-full border px-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-accent disabled:opacity-50 ${
                          r.filters_unlocked
                            ? 'justify-end border-emerald-600/70 bg-toggleOn shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]'
                            : 'justify-start border-zinc-600/80 bg-toggleOff'
                        }`}
                      >
                        <span
                          className={`h-5 w-5 shrink-0 rounded-full shadow-md ${
                            r.filters_unlocked ? 'bg-white' : 'bg-zinc-300'
                          }`}
                        />
                      </button>
                    ) : (
                      <span
                        className={`tabular-nums text-sm font-medium ${
                          r.filters_unlocked ? 'text-emerald-400' : 'text-mutedDim'
                        }`}
                        title="Only admins can change this"
                      >
                        {r.filters_unlocked ? 'On' : 'Off'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    {canEditCityFilters ? (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={Boolean(r.going_tonight_enabled)}
                        aria-label={`"Are you going tonight" enabled for ${r.name ?? r.id}`}
                        disabled={busyCityId === r.id}
                        onClick={() => void toggleGoingTonight(r, !r.going_tonight_enabled)}
                        className={`flex h-7 w-12 shrink-0 items-center rounded-full border px-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-accent disabled:opacity-50 ${
                          r.going_tonight_enabled
                            ? 'justify-end border-emerald-600/70 bg-toggleOn shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]'
                            : 'justify-start border-zinc-600/80 bg-toggleOff'
                        }`}
                      >
                        <span
                          className={`h-5 w-5 shrink-0 rounded-full shadow-md ${
                            r.going_tonight_enabled ? 'bg-white' : 'bg-zinc-300'
                          }`}
                        />
                      </button>
                    ) : (
                      <span
                        className={`tabular-nums text-sm font-medium ${
                          r.going_tonight_enabled ? 'text-emerald-400' : 'text-mutedDim'
                        }`}
                        title="Only admins can change this"
                      >
                        {r.going_tonight_enabled ? 'On' : 'Off'}
                      </span>
                    )}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* `CITY_HERO_CROP_ASPECT` moved 16:9 → 1:1 on 2026-08-01: the app stopped rendering the
          city hero as a 390×220 banner and now frames it SQUARE with `contentFit="cover"`
          (`screens/city.tsx:3565`), so a 16:9 crop was being center-cropped again on device and
          the admin lost ~44% of the width they'd composed. The modal still offers 4:3 / 16:9 /
          Original as presets — only the default moved. See `components/CityHeroPickerModal.tsx`. */}
      {cropPhoto && cropImageUrl ? (
        <VenuePhotoCropModal
          imageUrl={cropImageUrl}
          title="Crop before approving"
          aspect={CITY_HERO_CROP_ASPECT}
          busy={cropBusy}
          onCancel={() => {
            if (!cropBusy) setCropPhoto(null);
          }}
          onConfirm={(pixelCrop) => void confirmCropAndApproveCity(pixelCrop)}
        />
      ) : cropPhoto ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 text-sm text-muted">
          Loading image…
        </div>
      ) : null}

      {selectedCityId && selectedCity ? (
        <CityHeroPickerModal
          open={heroPickerOpen}
          cityId={selectedCityId}
          cityName={selectedCity.name}
          currentHeroUrl={selectedCityHeroUrl}
          adminUserId={user.id}
          photos={cityVenuePhotos}
          loading={cityVenuePhotosLoading}
          error={cityVenuePhotosError}
          onClose={() => setHeroPickerOpen(false)}
          onApplied={(heroUrl) => {
            // Mirror what admin_approve_city_photo just did server-side: new hero + Google fetch
            // off, and one more APPROVED city photo (so the "Using Google photo" dot flips).
            setRows((prev) =>
              prev.map((r) =>
                r.id === selectedCityId
                  ? { ...r, hero_image_url: heroUrl, google_fetch_disabled: true }
                  : r,
              ),
            );
            setApprovedCityPhotoCountByCity((prev) => {
              const next = new Map(prev);
              next.set(selectedCityId, (next.get(selectedCityId) ?? 0) + 1);
              return next;
            });
            setHeroPickerOpen(false);
            showSuccess('City photo updated');
          }}
        />
      ) : null}

      <CityVenueDetailModal
        venue={detailVenue}
        cityName={selectedCity?.name ?? null}
        onClose={() => setDetailVenue(null)}
      />

      {toastNode}
    </div>
  );
}
